import { node, isFocusable } from "~/util/dom";
import { startFocusTrap } from "~/util/focustrap";
import { useEscapeKey } from "~/util/hooks";

// TODO: bude-li nekdy treba dodat i moznost vlevo a vpravo, tak neni problem
export enum EPopOverPosition {
	BOTTOM,
	TOP,
}

export enum EPopOverFlow {
	START,
	CENTER,
	END,
}

export type PopOverMargin = [top: number, right: number, bottom: number, left: number];

export interface IPopOverProps {
	anchor: HTMLElement;
	popOverParent: HTMLElement;
	activator: HTMLElement;
	position: EPopOverPosition;
	flow: EPopOverFlow;
	margin: PopOverMargin;
	offset: PopOverMargin;
	content: HTMLElement | DocumentFragment;
	width: number;
	className: string;
	clickOutClose: boolean;
	escapeClose: boolean;
	clickOutIgnoredElements: Array<Element>;
	onClose: () => void;
	onOpen: () => void;
	focusTrap: boolean;
}

export const DEFAULT_PROPS: IPopOverProps = {
	anchor: null,
	popOverParent: document.body,
	activator: null,
	position: EPopOverPosition.BOTTOM,
	flow: EPopOverFlow.CENTER,
	margin: [0, 0, 0, 0],
	offset: [0, 0, 0, 0],
	content: null,
	width: 0,
	className: "",
	clickOutClose: true,
	escapeClose: true,
	clickOutIgnoredElements: [],
	onClose: null,
	onOpen: null,
	focusTrap: true,
};

export default class PopOver {
	protected popOver: HTMLDivElement;

	protected props: IPopOverProps;

	protected _isOpen: boolean;

	protected _cleanup: () => void;

	protected _stopFocusTrap: ReturnType<typeof startFocusTrap>;

	constructor(props: Partial<IPopOverProps> & Pick<IPopOverProps, "anchor" | "content">) {
		this.popOver = node("div", { className: `ui-popover ${props.className || ""}` });
		this.popOver.appendChild(props.content);

		this.props = {
			...DEFAULT_PROPS,
			activator: document.activeElement as HTMLElement,
			...props,
			clickOutIgnoredElements: [
				this.popOver,
				// pokud v props neprislo, ktere elementy ignorovat na clickOut, pak predpokladame nejtypictejsi chovani, tedy ignorovat anchor, coz byva ten, kdo akci vyvolal
				..."clickOutIgnoredElements" in props ? props.clickOutIgnoredElements : [props.anchor],
			],
		};
		this._isOpen = false;
	}

	get isOpen() {
		return this._isOpen;
	}

	destroy() {
		this.close();
	}

	addClass(className: string) {
		this.popOver.classList.add(className);
	}

	removeClass(className: string) {
		this.popOver.classList.remove(className);
	}

	handleEvent(event: PointerEvent) {
		switch (event.type) {
			case "pointerdown": {
				const target = event.target as Element;
				let isInIgnored = false;

				for (let index = 0; index < this.props.clickOutIgnoredElements.length; index++) {
					const ignored = this.props.clickOutIgnoredElements[index];

					if (ignored.contains(target)) {
						isInIgnored = true;
					}
				}

				if (!isInIgnored) {
					this.close();
				}

				break;
			}

			default:
				//
		}
	}

	close() {
		if (!this._isOpen) {
			return;
		}

		this._isOpen = false;
		this.popOver.remove();
		this.props.clickOutClose && window.removeEventListener("pointerdown", this, true);
		this._cleanup?.();
		if (this._stopFocusTrap) {
			this._stopFocusTrap();
			this._stopFocusTrap = undefined;
		}

		this.props.onClose && this.props.onClose();
	}

	protected placePopOver(anchorRect: DOMRect, position: EPopOverPosition) {
		let cssPopOver: Partial<CSSStyleDeclaration> = null;

		switch (position) {
			case EPopOverPosition.BOTTOM:
				this.popOver.style.removeProperty("bottom");
				cssPopOver = {
					top: `${anchorRect.top + anchorRect.height + this.props.offset[2]}px`,
					width: this.props.width > 0 ? `${this.props.width}px` : "auto",
				};

				this.popOver.style.setProperty("--top", cssPopOver.top);
				this.popOver.style.removeProperty("--bottom");
				break;

			case EPopOverPosition.TOP:
				this.popOver.style.removeProperty("top");
				cssPopOver = {
					bottom: `${window.innerHeight - anchorRect.top + this.props.offset[0]}px`,
					width: this.props.width > 0 ? `${this.props.width}px` : "auto",
				};
				this.popOver.style.setProperty("--bottom", cssPopOver.bottom);
				this.popOver.style.removeProperty("--top");
				break;

			default:
				//
		}

		Object.assign(this.popOver.style, cssPopOver);
	}

	open() {
		if (this._isOpen) {
			return;
		}

		this._isOpen = true;

		let position = this.props.position;
		const anchorRect = this.props.anchor.getBoundingClientRect();

		// do zacatku je popover neviditelny, protoze mozna bude menit pozici a tedy nechceme, aby ho uzivatel videl na spatnem miste driv
		this.popOver.classList.add("ui-popover--invisible");

		// uvodni napozicovani - optimisticky to dame tam, kam to patri dle zadani
		switch (this.props.flow) {
			case EPopOverFlow.CENTER: {
				// spocitame levou/pravou, protoze zname sirku predem
				let leftNumber = Math.round(anchorRect.left - (this.props.width / 2) + (anchorRect.width / 2));

				// precniva doprava? tak odsoupneme od praveho okraje
				leftNumber = leftNumber + this.props.width > window.innerWidth - this.props.margin[1] ? window.innerWidth - this.props.margin[1] - this.props.width : leftNumber;
				// precniva doleva? tak odsoupneme od leveho a tim padem muze teoreticky precnivat zase vpravo, ale to uz je smula
				leftNumber = leftNumber < this.props.margin[3] ? this.props.margin[3] : leftNumber;
				const left = `${leftNumber}px`;

				this.popOver.style.left = left;
				break;
			}

			case EPopOverFlow.START:
				this.popOver.style.left = `${anchorRect.left}px`;
				break;

			case EPopOverFlow.END:
				this.popOver.style.right = `${window.innerWidth - anchorRect.right}px`;
				break;

			default:
				//
		}

		this.placePopOver(anchorRect, position);
		this.props.popOverParent.appendChild(this.popOver);

		// korekce na druhou stranu, pokud se na tuhle nevejde (tedy pokud zkusime prohodit top a bottom, pripadne left a right)
		const popOverRect = this.popOver.getBoundingClientRect();

		switch (position) {
			case EPopOverPosition.BOTTOM:
				if (popOverRect.bottom > window.innerHeight - this.props.margin[2]) {
					position = EPopOverPosition.TOP;
					this.placePopOver(anchorRect, position);
				}
				break;

			case EPopOverPosition.TOP:
				if (popOverRect.top < this.props.margin[0]) {
					position = EPopOverPosition.BOTTOM;
					this.placePopOver(anchorRect, position);
				}
				break;

			default:
				//
		}

		// a ukazeme
		this.popOver.classList.remove("ui-popover--invisible");

		// klik kamkoliv mimo popover ho zavira
		this.props.clickOutClose && setTimeout(() => {
			window.addEventListener("pointerdown", this, true);
		}, 0);

		this.props.onOpen && this.props.onOpen();

		// focusneme prvniho potomka, ktery to umi
		setTimeout(() => {
			(document.activeElement as HTMLElement)?.blur?.();

			// escape zavira
			if (this.props.escapeClose) {
				this._cleanup = useEscapeKey(document, () => {
					this.close();
					this.props.activator && isFocusable(this.props.activator) && this.props.activator.focus();
				});
			}

			if (this.props.focusTrap) {
				this._stopFocusTrap = startFocusTrap(this.popOver);
			}

			/*
			Uz nefocusujeme prvniho potomka, protoze diky focus trap se na prvni tab stejne dostaneme,
			kam potrebujeme. Je to chyba UX, protoze chvili existuje stav, kdy uzivatel pohybujici se
			pomoci klavesnice visi ve vzduchoprazdnu (nic nema focus, ale vlastne ma to tlacitko, kterym
			to otevrel, takze se s tim da zit). Nicmene tim predejdeme focus-visible, ktere se nejak
			chybne ukazuje, i kdyz volame focus() programaticky.
			*/
		}, 0);
	}

	toggle() {
		if (this._isOpen) {
			this.close();
		} else {
			this.open();
		}
	}
}
